import { useEffect } from "react";
import type { LinksFunction } from "@remix-run/node";
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useNavigation } from
"@remix-run/react";
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import appStylesheetUrl from "~/styles/app.css";
import creatorSkeletonStylesheetUrl from "~/styles/creator-skeleton.css";
import { DashboardProvider } from "~/components/servers/DashboardContext";
import favicon from './favicon.ico';
import { initPostHog } from "~/lib/posthog";
import { PageSkeleton, isCreatorStudioPath } from "~/components/ui/creator_studio/pageSkeletons";

// Initialized at module scope (not inside a useEffect) so it runs before any
// component effect commits. React fires effects bottom-up on the initial
// mount — a child route's mount-time posthog.capture() would otherwise race
// ahead of this App component's own init effect and be silently dropped by
// posthog-js (capture calls issued before init() are not queued when the SDK
// is imported as an ES module rather than loaded via the snippet loader).
// This was the root cause of brand_invite_opened never reaching PostHog from
// onboarding.tsx's mount effect while every later capture on that same page
// (fired after user interaction) succeeded normally.
initPostHog();

const GTM_ID = "GTM-WVN38W83";

// TypeScript declaration for dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

export const links: LinksFunction = () => [
{ rel: "stylesheet", href: appStylesheetUrl },
// The navigation skeleton draws before the destination's own stylesheets
// arrive, so its styles ride with the root.
{ rel: "stylesheet", href: creatorSkeletonStylesheetUrl },
{ rel: "icon", href: "/favicon.ico" },
{
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap"
},
// Inter is the creator app's UI face, and the fallback for the finishing
// editor's captions and text layers. Those are TikTok Sans, self-hosted and
// linked only by the routes that draw them (`finishing/videoTextFonts.ts`).
// Preconnect + display=swap so it is fetched before first paint without
// blocking the app shell.
{ rel: "preconnect", href: "https://fonts.googleapis.com" },
{ rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
{
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;900&display=swap"
}];


/**
 * Google Tag Manager, started once the page has hydrated. It used to load from an
 * inline <head> script, so its tags ran while the HTML was still parsing and could
 * append <script> tags in the middle of the <body> Remix had rendered. React then
 * found a <script> where it expected a <link>, threw hydration error 418 and rebuilt
 * the whole document, <video> elements included, restarting their downloads
 * (2026-10-30: every load on an iPhone).
 */
let tagManagerStarted = false;
function startTagManager() {
  if (tagManagerStarted) return;
  tagManagerStarted = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

export default function App() {
  const location = useLocation();
  const navigation = useNavigation();
  const isNavigating = navigation.state !== 'idle';
  // Where the navigation in flight is going. A revalidation carries no
  // location, so it falls back to the page already on screen. Creator Studio
  // surfaces (and the profile home) get their own skeleton for the whole
  // transition — a form post there would otherwise flash the dark overlay
  // for its `submitting` phase and then the skeleton for `loading`.
  const pendingPathname = navigation.location?.pathname ?? location.pathname;
  const showCreatorStudioSkeleton = isNavigating && isCreatorStudioPath(pendingPathname);

  useEffect(() => {
    startTagManager();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'page_view',
          page_path: location.pathname + location.search
        });
      }
      posthog.capture('$pageview', { $current_url: window.location.href });
    }
  }, [location]);

  return (
    <html lang="en">
    <head>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1,viewport-fit=cover" />
      <link rel="icon" type="image/x-icon" href={favicon} />
      <Meta />
      <Links />
    </head>
    <body>
    {/* Google Tag Manager (noscript) - This stays at the top of <body> */}
    <noscript>
      <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }} />

    </noscript>

    <PostHogProvider client={posthog}>
    <DashboardProvider>
      <div className="flex min-h-screen">
        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    </DashboardProvider>
    </PostHogProvider>

    {/* Creator Studio loads as itself: the destination page's shell and card
             shapes, shimmering on the page ground — no scrim, no spinner, no copy
             (pageSkeletons.tsx). Its CSS animation-delay keeps it invisible for
             the first 120ms, so an instant navigation never flashes it. */}
    {showCreatorStudioSkeleton &&
        <div className="cs-skel-overlay" data-testid="route-skeleton" aria-hidden="true">
        <PageSkeleton pathname={pendingPathname} />
      </div>
        }

    {/* Navigation transition overlay for every other route (the brand
             dashboard and friends) — covers the background page flash while the
             next route loads. Unchanged. */}
    {isNavigating && !showCreatorStudioSkeleton &&
        <div data-testid="route-transition-overlay" style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: '#0f0f0f',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '20px',
          animation: 'sfn-fade-in 80ms ease-out forwards'
        }}>
        <style>{`
          @keyframes sfn-fade-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes sfn-pulse-ring {
            0%   { transform: scale(0.85); opacity: 0.6; }
            50%  { transform: scale(1);    opacity: 1; }
            100% { transform: scale(0.85); opacity: 0.6; }
          }
          @keyframes sfn-dot-bounce {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
            40%           { transform: scale(1);   opacity: 1; }
          }
        `}</style>

        {/* Pulsing ring */}
        <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            border: '2.5px solid rgba(255, 107, 53, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'sfn-pulse-ring 1.4s ease-in-out infinite'
          }}>
          <div style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ff6b35 0%, #ff8c5a 100%)',
              boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)'
            }} />
        </div>

        {/* Three bouncing dots */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {[0, 1, 2].map((i) =>
            <div key={i} style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: 'rgba(255, 107, 53, 0.7)',
              animation: `sfn-dot-bounce 1.2s ease-in-out ${i * 0.16}s infinite`
            }} />
            )}
        </div>
      </div>
        }

    <ScrollRestoration />
    <Scripts />
    <LiveReload />

    </body>
    </html>);

}